import { useState, useCallback, useRef, useEffect } from 'react';
import { ChatState, Message, ApiConfig } from '../types/chat';
import {
  ChatApiClient,
  generateSessionId,
  sanitizeMessage,
  validateMessage,
} from '../utils/api';
import useAuth from '@/contexts/useAuth';
import { ConversationMessage } from '../utils/dynamodb';

interface UseChatOptions {
  apiConfig?: Partial<ApiConfig>;
  maxMessages?: number;
  initialMessages?: ConversationMessage[];
  initialSessionId?: string;
  initialTitle?: string;
}

interface UseChatReturn {
  // State
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error';
  sessionId: string;
  conversationTitle: string | null;

  // Actions
  sendMessage: (content: string) => Promise<void>;
  clearChat: () => void;
  cancelRequest: () => void;
  loadConversation: (
    messages: ConversationMessage[],
    sessionId: string,
    title?: string,
  ) => void;

  // Internal functions for message management
  addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => string;
  updateMessage: (id: string, updates: Partial<Message>) => void;
  removeMessage: (id: string) => void;
}

const initialState: ChatState = {
  messages: [],
  isLoading: false,
  error: null,
  connectionStatus: 'disconnected',
  sessionId: generateSessionId(),
  conversationTitle: null,
};

/**
 * BE adds prefix: "USER QUESTION: "
 * and suffix: "\n\n---" and a whole bunch of stuff after that.
 * This helper in FE will trim the message of the prefix and suffix
 */
const trimPrefixSuffix = (content: string): string => {
  let trimmed = content;
  if (trimmed.startsWith('USER QUESTION: ')) {
    trimmed = trimmed.replace('USER QUESTION: ', '');
  }
  const suffixIndex = trimmed.indexOf('\n\n---');
  if (suffixIndex !== -1) {
    trimmed = trimmed.substring(0, suffixIndex);
  }
  return trimmed.trim();
};

/**
 * Converts any error message to a user-friendly format
 */
const getUserFriendlyErrorMessage = (error: unknown): string => {
  console.error('Error encountered:', error);
  // Default friendly message for any other error
  return 'Sorry, something went wrong. Please try again later.';
};

export function useChat(options: UseChatOptions = {}): UseChatReturn {
  const [state, setState] = useState<ChatState>(initialState);
  const apiClientRef = useRef<ChatApiClient | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const messageCounterRef = useRef(0);

  const auth = useAuth();
  // Use profile email if available, otherwise fallback to Cognito username (for admin users)
  const username = auth.userProfile?.email || auth.user?.username;

  // Initialise API client
  useEffect(() => {
    apiClientRef.current = new ChatApiClient(options.apiConfig);
    setState((prev) => ({
      ...prev,
      connectionStatus: 'connected',
    }));
  }, [options.apiConfig]);

  // Message management functions
  const addMessage = useCallback(
    (message: Omit<Message, 'id' | 'timestamp'>): string => {
      const id = String(messageCounterRef.current++);
      const newMessage: Message = {
        ...message,
        id,
        timestamp: new Date(),
      };

      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, newMessage],
      }));

      return id;
    },
    [],
  );

  const updateMessage = useCallback((id: string, updates: Partial<Message>) => {
    setState((prev) => ({
      ...prev,
      messages: prev.messages.map((msg) =>
        msg.id === id ? { ...msg, ...updates } : msg,
      ),
    }));
  }, []);

  const removeMessage = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      messages: prev.messages.filter((msg) => msg.id !== id),
    }));
  }, []);

  // Real-time streaming function
  const streamAgentSquadResponse = useCallback(
    async (messagePayload: any, streamingMessageId: string) => {
      const endpoint = apiClientRef.current?.getEndpoint();
      if (!endpoint) {
        throw new Error('No streaming endpoint available');
      }

      // Use root endpoint for Agent Squad responses
      const chatEndpoint = endpoint + '/chat';

      console.log('🌊 Connecting to streaming endpoint:', chatEndpoint);

      // Send the POST request to initiate streaming
      const response = await fetch(chatEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(messagePayload),
      });

      if (!response.ok) {
        throw new Error('Sorry, something went wrong. Please try again later.');
      }

      if (!response.body) {
        throw new Error(
          "Sorry, I didn't receive a response. Please try again later.",
        );
      }

      // Handle the JSON response from /stream endpoint
      const responseData = await response.json();
      console.log('📡 Agent Squad response received:', responseData);

      // Simulate streaming stages for better UX
      const stages = [
        {
          stage: 'routing',
          message: 'Ada is analysing your query...',
          delay: 300,
        },
        {
          stage: 'agent_selected',
          message: 'Routing to specialist agent...',
          delay: 500,
        },
        {
          stage: 'thinking',
          message: 'Agent is processing your request...',
          delay: 800,
        },
        {
          stage: 'generating',
          message: 'Generating response...',
          delay: 1000,
        },
      ];

      // Show streaming stages with delays
      for (let i = 0; i < stages.length; i++) {
        const stageInfo = stages[i];
        updateMessage(streamingMessageId, {
          content: stageInfo.message,
          streamingStage: stageInfo.stage as any,
          isLoading: true,
        });

        // Add delay between stages
        await new Promise((resolve) => setTimeout(resolve, stageInfo.delay));
      }

      // Show final response
      updateMessage(streamingMessageId, {
        content: responseData.output || 'No response received',
        streamingStage: 'complete',
        isLoading: false,
        citations: responseData.citations || [],
        debug_info: responseData.debug_info || {},
        traces: responseData.trace || [],
        collaboration_log: responseData.collaboration_log || [],
        agentId:
          responseData.metadata.agent_id ||
          responseData.metadata.agent_name ||
          'Agent Squad',
      });

      // Update conversation title if available
      if (responseData.conversation_title && !state.conversationTitle) {
        setState((prev) => ({
          ...prev,
          conversationTitle: responseData.conversation_title,
        }));
      }
    },
    [updateMessage, state.conversationTitle],
  );

  // Main sendMessage function - streaming only
  const sendMessage = useCallback(
    async (content: string) => {
      const sanitized = sanitizeMessage(content);

      if (!validateMessage(sanitized)) {
        setState((prev) => ({
          ...prev,
          error: 'Message is empty or too long (max 4000 characters)',
        }));
        return;
      }

      // Clear any previous error
      setState((prev) => ({ ...prev, error: null, isLoading: true }));

      // Add user message
      addMessage({
        sender: 'user',
        content: sanitized,
        type: 'text',
      });

      // Add streaming message that will be updated in real-time
      const streamingMessageId = addMessage({
        sender: 'agent',
        content: 'Initialising...',
        type: 'streaming',
        isLoading: true,
        streamingStage: 'initialising',
      });

      try {
        // Check if user is authenticated
        if (!username) {
          console.error('❌ No username available - user not authenticated');
          updateMessage(streamingMessageId, {
            content: 'Please sign in to start chatting.',
            isLoading: false,
            streamingStage: 'error',
          });
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: 'Authentication required',
          }));
          return;
        }

        setState((prev) => ({
          ...prev,
          connectionStatus: 'connecting',
        }));

        // Agent Squad spec: {content, user_id, session_id}
        const messagePayload = {
          query: sanitized,
          user_id: username,
          session_id: state.sessionId,
        };

        console.log(
          '🌊 Starting real-time Agent Squad streaming for user:',
          username,
        );

        // Start real-time streaming
        await streamAgentSquadResponse(messagePayload, streamingMessageId);

        setState((prev) => ({
          ...prev,
          isLoading: false,
          connectionStatus: 'connected',
        }));
      } catch (error) {
        console.error('❌ Streaming error:', error);

        // Ensure error message is always user-friendly
        const userMessage = getUserFriendlyErrorMessage(error);

        // Update streaming message to show error
        updateMessage(streamingMessageId, {
          content: userMessage,
          isLoading: false,
          streamingStage: 'error',
        });

        setState((prev) => ({
          ...prev,
          error: userMessage,
          isLoading: false,
          connectionStatus: 'disconnected',
        }));
      }
    },
    [
      state.sessionId,
      addMessage,
      updateMessage,
      streamAgentSquadResponse,
      username,
    ],
  );

  const clearChat = useCallback(() => {
    messageCounterRef.current = 0;
    setState((prev) => ({
      ...prev,
      messages: [],
      sessionId: generateSessionId(),
      error: null,
      conversationTitle: null,
    }));
  }, []);

  const cancelRequest = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setState((prev) => ({
      ...prev,
      isLoading: false,
      connectionStatus: 'disconnected',
    }));
  }, []);

  // Helper function to convert ConversationMessage to Message
  const convertConversationMessage = (
    msg: ConversationMessage,
    index: number,
  ): Message => {
    const timestamp =
      typeof msg.timestamp === 'number'
        ? new Date(
            msg.timestamp > 1000000000000
              ? msg.timestamp
              : msg.timestamp * 1000,
          )
        : new Date(msg.timestamp);

    const content = Array.isArray(msg.content)
      ? msg.content
          .map((part) => (typeof part === 'string' ? part : part.text || ''))
          .join('\n')
      : typeof msg.content === 'string'
        ? msg.content
        : '';

    return {
      id: String(index),
      sender: msg.role === 'user' ? 'user' : 'agent',
      content: trimPrefixSuffix(content),
      timestamp,
      type: 'text',
      citations: msg.citations || [],
    };
  };

  const loadConversation = useCallback(
    (messages: ConversationMessage[], sessionId: string, title?: string) => {
      const convertedMessages = messages.map(convertConversationMessage);

      messageCounterRef.current = convertedMessages.length;

      setState((prev) => ({
        ...prev,
        messages: convertedMessages,
        sessionId,
        conversationTitle: title || null,
        error: null,
      }));
    },
    [],
  );

  return {
    // State
    messages: state.messages,
    isLoading: state.isLoading,
    error: state.error,
    connectionStatus: state.connectionStatus,
    sessionId: state.sessionId,
    conversationTitle: state.conversationTitle,

    // Actions
    sendMessage,
    clearChat,
    cancelRequest,
    loadConversation,

    // Internal functions
    addMessage,
    updateMessage,
    removeMessage,
  };
}
